/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { getHasProductVersionInProductInventory } from 'pricing-pages-lib/utils/productInventoryUtils';
import { getCurrentSkuIdForProduct, getProductForSkuId } from 'pricing-pages-lib/utils/productsUtils';
import { useCallback, useContext } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { currentOrderedPaidProductLevels } from 'self-service-api/constants/ProductLevels';
import { CURRENT } from 'self-service-api/constants/VersionTypes';
import styled from 'styled-components';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { productVersionsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, productForSkuIdSelector, productVersionSelector, skuSelector } from '../../selectors/ProductsSelectors';
import { productDataSelector, productInventorySelector } from '../../selectors/SelectorUtils';
import { productNamesByProductLevelMapSelector } from '../../selectors/TrackingSelectors';
import classNames from '../../utils/classNames';
import HubProductLevelPicker from './HubProductLevelPicker';
import { PlanBuilderContext } from './PlanBuilder';
import PlanPickerHeader from './PlanPickerHeader';
import QuantityPacksPickerContainer from './QuantityPacksPickerContainer';
import { seleniumUnitQuantitiesStringSelector, seleniumUnitsTypesStringSelector } from './SeleniumUtils';
const StyledPlanPickerWrapper = styled.div.withConfig({
  displayName: "HubPlanPickerList__StyledPlanPickerWrapper",
  componentId: "sc-1eg49vu-0"
})(["display:flex;flex-direction:column;flex:1;"]);

const PlanPicker = ({
  productLine,
  className,
  useCompactTitle
}) => {
  const {
    productConfigurations,
    setProductConfigurations,
    requiredProductLines,
    trackingSource
  } = useContext(PlanBuilderContext);
  const currencyCode = useSelector(activeCurrencySelector);
  const required = requiredProductLines.includes(productLine);
  const ownedSkuId = useSelector(state => {
    const ownedProductVersion = productVersionsFromProductInventorySelector(state).find(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      return product.productLine === productLine && product.productLevel;
    });
    return ownedProductVersion && ownedProductVersion.skuId;
  });
  const ownedCurrentSkuId = useSelector(state => {
    if (!ownedSkuId) return null;
    const {
      versionType
    } = skuSelector(state, {
      skuId: ownedSkuId
    });
    return versionType === CURRENT && ownedSkuId;
  });
  const selectedSkuId = useSelector(state => {
    const foundConfig = productConfigurations.find(({
      skuId
    }) => {
      const product = productForSkuIdSelector(state, {
        skuId
      });
      return product.productLine === productLine && product.productLevel;
    });
    let updatedOwnedSkuId;

    if (ownedCurrentSkuId) {
      const {
        productId
      } = skuSelector(state, {
        skuId: ownedCurrentSkuId
      });
      updatedOwnedSkuId = ownedCurrentSkuId && currentSkuIdForProductSelector(state, {
        productId
      });
    }

    return foundConfig ? foundConfig.skuId : updatedOwnedSkuId;
  });
  const ownedProductLevel = useSelector(state => ownedCurrentSkuId && productForSkuIdSelector(state, {
    skuId: ownedCurrentSkuId
  }).productLevel); // This can be different than ownedProductLevel if you own a legacy product (sidegrades)

  const disabledProductLevel = useSelector(state => {
    if (!ownedSkuId) return null;
    const {
      productLevel
    } = productForSkuIdSelector(state, {
      skuId: ownedSkuId
    });
    return productLevel === currentOrderedPaidProductLevels[0] ? null : currentOrderedPaidProductLevels[currentOrderedPaidProductLevels.indexOf(productLevel) - 1];
  });
  const selectedProductLevel = useSelector(state => selectedSkuId && productForSkuIdSelector(state, {
    skuId: selectedSkuId
  }).productLevel);
  const trackingProductNamesByProductLevelMap = useSelector(state => productNamesByProductLevelMapSelector(state, {
    productLine
  }), shallowEqual);
  const productData = useSelector(productDataSelector);
  const productInventory = useSelector(productInventorySelector);
  const setProductLevel = useCallback(newProductLevel => {
    if (!newProductLevel) {
      if (selectedProductLevel === ownedProductLevel) {
        return;
      } else {
        newProductLevel = ownedProductLevel;
      }
    }

    if (newProductLevel) {
      getUsageTracker().track('clickMerchSelectionButton', {
        currency: currencyCode,
        productName: trackingProductNamesByProductLevelMap[newProductLevel],
        trackingSource
      });
    }

    setProductConfigurations(prevConfigs => {
      const filteredConfigs = prevConfigs.filter(({
        skuId
      }) => {
        const product = getProductForSkuId({
          productData
        }, {
          skuId
        });
        return !product.productLine || product.productLine !== productLine || !product.productLevel;
      });

      if (newProductLevel) {
        const newSkuId = getCurrentSkuIdForProduct({
          productData
        }, {
          productLine,
          productLevel: newProductLevel
        });

        if (!getHasProductVersionInProductInventory({
          productInventory,
          productData
        }, {
          skuId: newSkuId
        })) {
          filteredConfigs.push(createProductConfig({
            skuId: newSkuId
          }));
        }
      }

      return filteredConfigs;
    });
  }, [productData, productLine, setProductConfigurations]);
  const quantityPackDetails = useSelector(state => selectedSkuId ? productVersionSelector(state, {
    skuId: selectedSkuId
  }).quantityPackDetails : []);
  const seleniumUnitsString = useSelector(state => seleniumUnitQuantitiesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const seleniumUnitsTypesIdString = useSelector(state => seleniumUnitsTypesStringSelector(state, {
    productConfigurations,
    productVersionSkuId: selectedSkuId
  }));
  const apiName = useSelector(state => {
    const product = selectedSkuId && productForSkuIdSelector(state, {
      skuId: selectedSkuId
    });
    return product && product.apiName;
  });
  return /*#__PURE__*/_jsxs("div", {
    className: className,
    "data-product-level": selectedProductLevel,
    "data-selenium-id": `${productLine}-plan-builder-picker`,
    "data-api-name": apiName,
    "data-units": seleniumUnitsString || '0',
    "data-units-type": seleniumUnitsTypesIdString,
    children: [/*#__PURE__*/_jsx(PlanPickerHeader, {
      productLine: productLine,
      compact: useCompactTitle,
      trackingSource: trackingSource
    }), /*#__PURE__*/_jsx(HubProductLevelPicker, {
      productLine: productLine,
      required: required,
      selectedProductLevel: selectedProductLevel,
      ownedProductLevel: ownedProductLevel,
      disabledProductLevel: disabledProductLevel,
      setSelectedProductLevel: setProductLevel
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: quantityPackDetails.length > 0,
      children: /*#__PURE__*/_jsx(QuantityPacksPickerContainer, {
        quantityPackSkuIds: quantityPackDetails.map(({
          skuId
        }) => skuId),
        setProductLevel: setProductLevel,
        trackingSource: trackingSource
      })
    })]
  });
};

const HubPlanPickerList = ({
  productLines,
  useCompactTitle
}) => {
  return /*#__PURE__*/_jsx(StyledPlanPickerWrapper, {
    children: productLines.map((productLine, index) => /*#__PURE__*/_jsx(PlanPicker, {
      productLine: productLine,
      className: classNames(index > 0 && 'm-top-10'),
      useCompactTitle: useCompactTitle
    }, productLine))
  });
};

export default HubPlanPickerList;